import { EmailProviderIdEnum, FeatureFlagsKeysEnum } from '@novu/shared';
import { useMemo, useState } from 'react';
import { RiInformation2Line, RiKey2Line } from 'react-icons/ri';
import { type AgentIntegrationLink, type AgentResponse } from '@/api/agents';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/primitives/tooltip';
import { IS_SELF_HOSTED_EE } from '@/config';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useFetchIntegrations } from '@/hooks/use-fetch-integrations';
import { InboundAddressConfig } from './inbound-address-config';
import { OutboundProviderSelect } from './outbound-provider-select';
import {
  IntegrationCredentialsSidebar,
  ListeningStatus,
  ProviderSetupStepperRail,
  SetupButton,
  SetupStep,
  SetupStepperRail,
} from './setup-guide-primitives';
import { deriveStepStatus } from './setup-guide-step-utils';
import { SharedInboundAddressField } from './shared-inbound-address-field';
import { useEmailSetupCredentials } from './use-email-setup-credentials';

export type EmailSetupGuideProps = {
  agent: AgentResponse;
  integrationId: string;
  stepOffset?: number;
  onStepsCompleted?: () => void;
  embedded?: boolean;
  /**
   * Optional agent–integration link, populated by callers that already have
   * it. When present, the wizard counts the Novu shared inbox as a valid
   * inbound address. When absent (legacy callers), the wizard falls back to
   * the previous behavior of requiring a custom address.
   */
  integrationLink?: AgentIntegrationLink;
  /** Onboarding hides the custom-address add-form; the shared inbox is enough to get started. */
  isOnboarding?: boolean;
};

export function EmailSetupGuide({
  agent,
  integrationId,
  stepOffset = 1,
  onStepsCompleted,
  embedded = false,
  integrationLink,
  isOnboarding = false,
}: EmailSetupGuideProps) {
  const { integrations } = useFetchIntegrations();
  const isEmailWhatsNextEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_AGENT_EMAIL_WHATS_NEXT_ENABLED);

  const [isCredentialsSidebarOpen, setIsCredentialsSidebarOpen] = useState(false);
  const [testConnected, setTestConnected] = useState(false);

  const emailIntegration = useMemo(
    () => integrations?.find((i) => i._id === integrationId && i.providerId === EmailProviderIdEnum.NovuAgent),
    [integrations, integrationId]
  );

  const {
    outboundId,
    configuredAddresses,
    domains,
    needsCredentialsStep,
    hasOutboundCredentials,
    outboundProviderConfig,
    onOutboundSelect,
    addAddress,
    removeAddress,
  } = useEmailSetupCredentials({ emailIntegration, integrations, agent });

  /**
   * Cloud-provisioned shared inbox address for this agent (e.g.
   * `support-agent-x@agentconnect.sh`). Pulled from the link payload because
   * computing it client-side would require the `NOVU_AGENT_SHARED_INBOUND_DOMAIN`
   * env var, which is server-only.
   */
  const sharedInboundAddress = integrationLink?.integration?.sharedInboundAddress;
  const sharedInboxDisabled = Boolean(integrationLink?.integration?.sharedInboxDisabled);
  const hasSharedInbox = Boolean(sharedInboundAddress) && !sharedInboxDisabled;

  // Layer-1 simplification: on cloud (a shared inbox is provisioned), the agent works out of the
  // box on the Novu demo sender + shared inbox, so setup collapses to "see your address + test".
  // Production hardening (own provider, custom domain, branded address) moves to the layer-2
  // "What's next" guide; custom From lives in the EMAIL card below. Self-hosted EE has no demo/shared inbox, so it keeps the full
  // provider + domain flow as a hard prerequisite. Flag off = unchanged behavior.
  const simplifiedSetup = isEmailWhatsNextEnabled && hasSharedInbox && !IS_SELF_HOSTED_EE;

  // Step indices: credentials step is conditionally inserted
  const base = stepOffset;
  const credentialsStepIndex = base + 1;
  const inboundStepIndex = needsCredentialsStep ? base + 2 : base + 1;
  const testStepIndex = inboundStepIndex + 1;

  // The Novu shared inbox satisfies the "configure inbound address" step out
  // of the box on cloud; users can still add custom-domain routes for branded
  // delivery, but they're no longer a hard prerequisite.
  const hasAddresses = configuredAddresses.length > 0 || hasSharedInbox;

  // The "Setup providers to send emails" step starts complete: the agent
  // already has the Novu demo sender selected by default, and choosing a real
  // provider is an explicit upgrade rather than a prerequisite. For demo,
  // `needsCredentialsStep` stays false so the credentials step is skipped
  // naturally and the wizard advances straight to the inbound-address step.
  // Self-hosted Enterprise has no bundled Novu demo sender, so a freshly
  // provisioned agent has no outbound provider selected. Treat picking one as a
  // hard prerequisite there. On cloud the demo is selected by default, so this
  // branch never fires and the step stays pre-completed exactly as before (zero
  // behavioral delta).
  const needsOutboundSelection = IS_SELF_HOSTED_EE && !outboundId;

  const firstIncompleteStep = useMemo(() => {
    if (needsOutboundSelection) return base;
    if (needsCredentialsStep && !hasOutboundCredentials) return credentialsStepIndex;
    if (!hasAddresses) return inboundStepIndex;
    if (!testConnected) return testStepIndex;

    return testStepIndex + 1;
  }, [
    needsOutboundSelection,
    base,
    needsCredentialsStep,
    hasOutboundCredentials,
    credentialsStepIndex,
    hasAddresses,
    inboundStepIndex,
    testStepIndex,
    testConnected,
  ]);

  const showInboundAddressOnTestStep = isOnboarding && hasSharedInbox && sharedInboundAddress;

  const stepsColumn = (
    <>
      <SetupStep
        index={base}
        status={deriveStepStatus(base, firstIncompleteStep)}
        sectionLabel="SETUP SENDING EMAILS"
        title="Setup providers to send emails."
        description={
          IS_SELF_HOSTED_EE
            ? 'Select an email provider (e.g. SendGrid, SES, Resend) so your agent can send replies.'
            : 'The Novu Email demo sender is used by default so your agent can reply out of the box. Switch to your own provider for higher volume later.'
        }
        rightContent={
          <div className="flex w-full flex-col gap-1.5">
            <div className="text-text-strong text-label-xs flex items-center gap-1 font-medium leading-4">
              <span>Send emails via</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="More info" className="inline-flex">
                    <RiInformation2Line className="text-text-soft size-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Select the email provider you want to use to send emails.
                </TooltipContent>
              </Tooltip>
            </div>
            <OutboundProviderSelect selectedId={outboundId || undefined} onSelect={onOutboundSelect} hideLabel />
            <div className="flex items-center gap-1">
              <RiInformation2Line className="text-text-soft size-3.5" aria-hidden />
              <span className="text-text-soft text-label-xs font-normal leading-4">
                You can setup other providers later.{' '}
              </span>
            </div>
          </div>
        }
      />

      {needsCredentialsStep && (
        <SetupStep
          index={credentialsStepIndex}
          status={deriveStepStatus(credentialsStepIndex, firstIncompleteStep)}
          title={`Configure ${outboundProviderConfig?.displayName ?? 'provider'} credentials`}
          description={
            <span>
              {'Paste API keys or credentials from your email provider into the integration. '}
              {outboundProviderConfig?.docReference && (
                <a
                  href={outboundProviderConfig.docReference}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-sub underline"
                >
                  View setup guide
                </a>
              )}
            </span>
          }
          rightContent={
            <SetupButton
              leadingIcon={<RiKey2Line className="size-3.5" />}
              onClick={() => setIsCredentialsSidebarOpen(true)}
            >
              Configure credentials
            </SetupButton>
          }
        />
      )}

      <SetupStep
        index={inboundStepIndex}
        status={deriveStepStatus(inboundStepIndex, firstIncompleteStep)}
        sectionLabel="SETUP RECEIVING EMAILS"
        title="Configure inbound address"
        description={
          showInboundAddressOnTestStep
            ? 'Your agent receives email on a dedicated inbound address. Custom domains and providers can be configured later.'
            : 'Email sent to this address is delivered to your agent.'
        }
        extraContent={
          showInboundAddressOnTestStep ? undefined : (
            <InboundAddressConfig
              sharedInboundAddress={hasSharedInbox ? sharedInboundAddress : undefined}
              configuredAddresses={configuredAddresses}
              domains={domains}
              onAddAddress={addAddress}
              onRemoveAddress={removeAddress}
              hideCustomAddressForm={isOnboarding}
            />
          )
        }
      />

      <SetupStep
        index={testStepIndex}
        status={deriveStepStatus(testStepIndex, firstIncompleteStep)}
        title="Test connection"
        description="Send an email to your configured inbound address. We will detect when it arrives."
        extraContent={
          <div className="flex w-full flex-col gap-4">
            {showInboundAddressOnTestStep ? (
              <SharedInboundAddressField sharedInboundAddress={sharedInboundAddress} />
            ) : null}
            <ListeningStatus
              inline
              agentIdentifier={agent.identifier}
              watchedIntegrationId={integrationId}
              onConnected={() => {
                setTestConnected(true);
                onStepsCompleted?.();
              }}
              connectedMessage="Your email integration is connected. This agent is ready to receive emails."
              listeningMessage="Waiting for your email: send a message to your configured inbound address."
            />
          </div>
        }
      />
    </>
  );

  // Two-step out-of-box flow used when `simplifiedSetup` is on: the address is provisioned, so the
  // only action is the connection test. Everything production-oriented lives in "What's next".
  const simplifiedFirstIncompleteStep = testConnected ? base + 2 : base + 1;

  const simplifiedStepsColumn = (
    <>
      <SetupStep
        index={base}
        status={deriveStepStatus(base, simplifiedFirstIncompleteStep)}
        sectionLabel="YOUR AGENT'S EMAIL"
        title="Your agent's email address"
        description="Your agent receives email on this dedicated inbound address. Share it with your users to start a conversation. Connect your own provider and a custom domain later from What's next."
        extraContent={
          sharedInboundAddress ? <SharedInboundAddressField sharedInboundAddress={sharedInboundAddress} /> : undefined
        }
      />

      <SetupStep
        index={base + 1}
        status={deriveStepStatus(base + 1, simplifiedFirstIncompleteStep)}
        title="Test connection"
        description="Send an email to your inbound address. We will detect when it arrives."
        extraContent={
          <div className="flex w-full flex-col gap-4">
            <ListeningStatus
              inline
              agentIdentifier={agent.identifier}
              watchedIntegrationId={integrationId}
              onConnected={() => {
                setTestConnected(true);
                onStepsCompleted?.();
              }}
              connectedMessage="Your email integration is connected. This agent is ready to receive emails."
              listeningMessage="Waiting for your email: send a message to your inbound address."
            />
          </div>
        }
      />
    </>
  );

  const activeStepsColumn = simplifiedSetup ? simplifiedStepsColumn : stepsColumn;

  const credentialsSidebar =
    outboundId && needsCredentialsStep ? (
      <IntegrationCredentialsSidebar
        integrationId={outboundId}
        isOpen={isCredentialsSidebarOpen}
        onClose={() => setIsCredentialsSidebarOpen(false)}
        agentOnboarding
      />
    ) : null;

  if (embedded) {
    return (
      <div className="flex flex-col gap-0">
        <SetupStepperRail className="py-6 pb-3 pr-3 md:pr-6">{activeStepsColumn}</SetupStepperRail>
        {credentialsSidebar}
      </div>
    );
  }

  return (
    <>
      <ProviderSetupStepperRail>{activeStepsColumn}</ProviderSetupStepperRail>
      {credentialsSidebar}
    </>
  );
}
